import React, { FC } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { useNavigate } from 'react-router-dom';
import { Section } from 'src/components/widgets/section';
import { NoIntegrationDialog } from 'src/shared/dialogs';
import { backofficeUrl, cartesianProduct, getUrlWithQueryParams, T } from 'src/shared/helpers';
import AddProductNoEcommerce from 'src/shared/icons/no-ecommerce.svg';
import AddProductFromZakeke from 'src/shared/icons/store.svg';
import styled from 'styled-components';
import { Link } from '../../../../../widgets/buttons';
import { useDialogManager } from '../../../../../widgets/dialog';
import { PageFooterButtons } from '../../../../../widgets/headers';
import { VerticalLayout } from '../../../../../widgets/layouts';
import { showNotification } from '../../../../../widgets/notificationToaster';
import { PageHeader } from '../../../../../widgets/pageHeader';
import { SelectionType, SelectionTypeContainer } from '../../../../../widgets/selectionType';
import { ContainerSmall } from '../../../containers';
import { addNewColorAction, useProductConfigurationState } from '../../../state';
import SelectedOptionValue from '../../../state/selectedOptionValue';
import ConnectModelDialog, { PartialSalesChannel } from '../../6-done/connectModelDialog';
import { menuSourceVariantsQuery } from './__generated__/menuSourceVariantsQuery.graphql';

interface MenuSourceVariantsProps {
    onChooseImport: () => void;
    onChooseScratch: () => void;
    productId: string;
}

const PageHeaderContainer = styled.div`
    margin: 20px 0px !important;

    > div {
        padding-bottom: 0px !important;
    }
`;

const MenuSourceVariantsSection = styled(Section)`
    height: calc(100vh - 310px);
    > div {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }
`;

const MenuSourceVariantContainerSmall = styled(ContainerSmall)`
    margin: unset;
`;

const GuideLink = styled.div`
    text-align: center;
    margin-bottom: 20px;
    font-size: 14px;

    p {
        margin: 0;
    }

    a {
        display: inline-block;
        margin-top: 8px;
    }
`;

const MenuSourceVariants: FC<MenuSourceVariantsProps> = ({ onChooseImport, onChooseScratch, productId }) => {
    const { state, dispatch } = useProductConfigurationState();
    const { showDialog, closeDialog } = useDialogManager();
    const navigate = useNavigate();
    const { salesChannels } = useLazyLoadQuery<menuSourceVariantsQuery>(
        graphql`
            query menuSourceVariantsQuery {
                salesChannels(status: ACTIVE) {
                    id
                    ecommerceSettings {
                        ecommerceID
                        ecommerceName
                        isMarketplace
                        hasProductsApi
                        remoteUrl
                        definitiveUrl
                    }
                }
            }
        `,
        {}
    );

    const onImportVariants = () => {
        if (salesChannels.some((s) => s.ecommerceSettings?.ecommerceName !== 'NONE')) {
            const realSalesChannels = salesChannels.filter((s) => s.ecommerceSettings?.ecommerceName !== 'NONE');
            showDialog(
                'connectProductDialog',
                <ConnectModelDialog
                    additionalCodes={[]}
                    merchantProductOptionsConnected={[]}
                    productColors={[]}
                    productID={''}
                    forImportingVariants={true}
                    salesChannels={realSalesChannels as PartialSalesChannel[]}
                    productSalesChannelID={state.salesChannelID}
                    onConfirmImporting={(sourceMerchantProductOptions) => {
                        if (sourceMerchantProductOptions.filter((opt) => opt.optionID && opt.optionID != '').length > 0) {
                            let variantsImported = sourceMerchantProductOptions.map((attr) =>
                                attr.values.map((opt) => {
                                    return { Id: attr.optionID, Label: attr.label, Value: { Id: opt.valueID, Label: opt.label } };
                                })
                            );
                            let variantsToSave =
                                sourceMerchantProductOptions.length == 1 ? variantsImported[0].map((v) => [v]) : cartesianProduct(...variantsImported);
                            variantsToSave.forEach((variant: SelectedOptionValue[]) => {
                                dispatch(addNewColorAction(variant));
                            });

                            if (onChooseImport) onChooseImport();
                        } else {
                            showNotification({
                                type: 'danger',
                                message: <>{T._('Unable to import: product without variants.', 'Admin')}</>,
                                dismiss: {
                                    duration: 3000
                                }
                            });
                        }
                        closeDialog('connectProductDialog');
                    }}
                />
            );

            return;
        } else if (!salesChannels.some((s) => s.ecommerceSettings?.ecommerceName !== 'NONE')) {
            showDialog(
                'complete-ecommerce-integration',
                <NoIntegrationDialog
                    subTitleNoIntegration={T._('Unable to import variants', 'Admin')}
                    message={
                        <label>{T._('Complete the integration with Zakeke by selecting your ecommerce platform and connecting your store.', 'Admin')}</label>
                    }
                ></NoIntegrationDialog>
            );
            return;
        }
    };

    return (
        <VerticalLayout>
            <PageHeaderContainer>
                <PageHeader
                    isBackVisible={false}
                    title={T._('Attribute setup', 'Admin')}
                    onBackClick={() => navigate(getUrlWithQueryParams(backofficeUrl('/product/print-area/')))}
                ></PageHeader>
            </PageHeaderContainer>
            <MenuSourceVariantsSection>
                <MenuSourceVariantContainerSmall isFullWidth={true}>
                    <GuideLink>
                        <p>{T._('Choose how you want to define the product variants:', 'Admin')}</p>
                        <p>{T._('create them manually from scratch or import them from your store.', 'Admin')}</p>
                        <Link
                            target='_blank'
                            href='https://zakeke.zendesk.com/hc/en-us/articles/360015501299-Variants-Detailed-guide-on-setting-them-up-for-your-customizable-product#h_01K3NB1SE7BYZKC9HBDNVQ5D1V'
                        >
                            {T._('Learn more about setting up variants.', 'Admin')}
                        </Link>
                    </GuideLink>
                    <SelectionTypeContainer>
                        <SelectionType key='scratch' label={T._('Scratch', 'Admin')} icon={<AddProductNoEcommerce />} onClick={onChooseScratch} />
                        <SelectionType key='import' label={T._('Import', 'Admin')} icon={<AddProductFromZakeke />} onClick={onImportVariants}></SelectionType>
                    </SelectionTypeContainer>
                </MenuSourceVariantContainerSmall>
            </MenuSourceVariantsSection>
            <PageFooterButtons $fixed={true}></PageFooterButtons>
        </VerticalLayout>
    );
};

export default MenuSourceVariants;
